
import React, { useContext, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";

import { AppContext } from "../../App";
import * as actions from "../../api/actions/pos";
import { controller, endpoints } from "../../api/actions/api";
import { safeOnComplete } from "../../utils/safeOnComplete";

import {
  getPrintAgentStatus,
  buildReportHtml,
  printReportHtmlDirect,
} from "../../api/actions/printAgent";

const PAPER = {
  A4: { width: 210, height: 297 },
  Letter: { width: 215.9, height: 279.4 },
};

const ReportPrintButton = ({
  _store,
  _get,
  reportRef,
  label,
  reportTitle,
  paperSize = "Letter",
  onResult,
}) => {
  const { t } = useTranslation();
  const { setCLoader } = useContext(AppContext);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [selectedPaper, setSelectedPaper] = useState(paperSize);
  const [orientation, setOrientation] = useState("portrait");
  const [scale, setScale] = useState(90);

  const notify = (type, title, message) => {
    if (onResult) {
      onResult({
        isOpen: true,
        type,
        title,
        subTitle: message,
      });
    } else if (type !== "success") {
      window.alert(message);
    }
  };

  const previewHtml = useMemo(() => {
    if (!previewOpen || !reportRef?.current) return "";

    let html = buildReportHtml(
      reportRef.current,
      selectedPaper
    );

    // Reset off-screen printable report positioning in preview HTML
    html = html.replace(
      "</head>",
      `<style>
        .products-sales-volume-print,
        .purchase-report-print,
        .register-report-print,
        .expense-report-print,
        .employee-report-print,
        .top-selling-print,
        .return-products-print,
        .product-report-print,
        .refund-report-print,
        .store-report-print,
        .register-details-print,
        .categories-report-print,
        .category-details-print,
        .customers-report-print,
        .customer-details-print,
        .products-list-print,
        .product-details-print,
        .expense-categories-print,
        .expense-category-details-print,
        .expense-category-edit-print,
        .pay-bills-print,
        .pay-bill-details-print,
        .employee-history-print,
        .employee-history-details-print,
        .salary-history-print,
        .salary-history-details-print,
        .salary-payment-form-print,
        .users-list-print,
        .user-details-print,
        .inventory-list-print,
        .inventory-details-print,
        .purchase-returns-print,
        .purchase-return-details-print,
        .purchases-list-print,
        .purchase-details-print,
        .sales-transactions-print,
        .sale-details-print,
        .stock-transfers-print,
        .stock-transfer-details-print,
        .stores-list-print,
        .store-details-print,
        .suppliers-list-print,
        .supplier-details-print {
          position: static !important;
          left: auto !important;
          top: auto !important;
          width: 100% !important;
          max-width: 100% !important;
          box-sizing: border-box !important;
        }
      </style></head>`
    );


    // Remove Search controls from Products Sales Volume print HTML
    if (
      reportRef.current.classList.contains(
        "products-sales-volume-print"
      )
    ) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");

      doc.querySelectorAll("input").forEach((input) => {
        const placeholder = (
          input.getAttribute("placeholder") || ""
        ).toLowerCase();

        const ariaLabel = (
          input.getAttribute("aria-label") || ""
        ).toLowerCase();

        const isSearch =
          input.type === "search" ||
          placeholder.includes("search") ||
          ariaLabel.includes("search");

        if (!isSearch) return;

        // Remove the complete search control when possible
        const container =
          input.closest(".MuiFormControl-root") ||
          input.closest(".MuiInputBase-root") ||
          input.parentElement;

        if (container) {
          container.remove();
        } else {
          input.remove();
        }
      });

      html = doc.documentElement.outerHTML;
    }

  const extraStyle = `
    <style>
      @page {
        size: ${selectedPaper} ${orientation};
        margin: 12mm;
      }

      /* Apply scale to the printable report */
      body {
        zoom: ${scale / 100};
      }

      /* Hide Search box in Products Sales Volume print */
      .products-sales-volume-print input[type="search"],
      .products-sales-volume-print input[placeholder*="Search"],
      .products-sales-volume-print input[placeholder*="search"] {
        display: none !important;
      }
        
    </style>
  `;

    return html.replace("</head>", `${extraStyle}</head>`);
  }, [previewOpen, selectedPaper, orientation, scale, reportRef]);

  const printReport = async () => {
    if (printing) return;

    if (!_store?.id) {
      notify("warning", t("printer"), t("noStoreSelected"));
      return;
    }

    setPrinting(true);
    setCLoader(true);

    try {
      const agentStatus = await getPrintAgentStatus();

      if (!agentStatus.running) {
        notify(
          "warning",
          t("printAgentNotRunning"),
          t("printAgentNotRunningMessage")
        );
        return;
      }

      // Type 2: Report printer only.
      const printer = await new Promise((resolve, reject) => {
        const onComplete = safeOnComplete(
          (res) => {
            if (res?.status === 200 && res?.result?.printerName) {
              resolve(res.result);
            } else {
              reject(new Error(t("noDefaultReportPrinter")));
            }
          },
          (err) => {
            reject(
              new Error(
                err?.msg || t("noDefaultReportPrinter")
              )
            );
          }
        );

        _get(
          endpoints.StorePrinter +
            controller.GetDefaultPrinter +
            `/${_store.id}/2`,
          null,
          onComplete
        );
      });

      // Send the same HTML used in the preview.
      await printReportHtmlDirect(
        printer.printerName,
        previewHtml,
        selectedPaper,
        orientation
      );

      setPreviewOpen(false);

      notify(
        "success",
        t("successful"),
        t("reportPrintAccepted")
      );
    } catch (err) {
      console.error("REPORT PRINT ERROR:", err);

      notify(
        "warning",
        t("printer"),
        err?.message || t("receiptNotPrintedMessage")
      );
    } finally {
      setPrinting(false);
      setCLoader(false);
    }
  };

  const page = PAPER[selectedPaper] || PAPER.Letter;
  const isLandscape = orientation === "landscape";
  const pageWidth = isLandscape ? page.height : page.width;
  const pageHeight = isLandscape ? page.width : page.height;

  return (
    <>
      <Button
        variant="contained"
        onClick={() => setPreviewOpen(true)}
      >
        {label || t("print")}
      </Button>

      <Dialog
        open={previewOpen}
        onClose={() => !printing && setPreviewOpen(false)}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle>
          {t("print")} — {reportTitle || t("report")}
        </DialogTitle>

        <DialogContent dividers>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            sx={{ mb: 2 }}
          >
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Paper Size</InputLabel>
              <Select
                label="Paper Size"
                value={selectedPaper}
                onChange={(e) => setSelectedPaper(e.target.value)}
              >
                <MenuItem value="Letter">US Letter</MenuItem>
                <MenuItem value="A4">A4</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Orientation</InputLabel>
              <Select
                label="Orientation"
                value={orientation}
                onChange={(e) => setOrientation(e.target.value)}
              >
                <MenuItem value="portrait">Portrait</MenuItem>
                <MenuItem value="landscape">Landscape</MenuItem>
              </Select>
            </FormControl>

            <Box sx={{ width: 220 }}>
              <Typography variant="body2">
                Scale: {scale}%
              </Typography>
              <Slider
                min={60}
                max={100}
                step={5}
                value={scale}
                onChange={(_, value) => setScale(value)}
                valueLabelDisplay="auto"
              />
            </Box>
          </Stack>

          <Box
            sx={{
              bgcolor: "#e5e5e5",
              p: 2,
              overflow: "auto",
              maxHeight: "65vh",
              textAlign: "center",
            }}
          >

            <Box
            sx={{
                width: `${pageWidth}mm`,
                minHeight: `${pageHeight}mm`,
                mx: "auto",
                bgcolor: "#fff",
                boxShadow: 3,
                overflow: "hidden",
            }}
            >
            <iframe
                title="Report Print Preview"
                srcDoc={previewHtml}
                scrolling="no"
                onLoad={(event) => {
                const iframe = event.currentTarget;

                try {
                    const doc = iframe.contentDocument;

                    if (!doc) return;

                    const contentHeight = Math.max(
                    doc.body.scrollHeight,
                    doc.documentElement.scrollHeight
                    );

                    const pageHeightPx =
                    (pageHeight / 25.4) * 96;

                    const pageCount = Math.max(
                    1,
                    Math.ceil(contentHeight / pageHeightPx)
                    );

                    iframe.style.height =
                    `${pageCount * pageHeightPx}px`;
                } catch (err) {
                    console.error("Report preview resize:", err);
                }
                }}
                style={{
                display: "block",
                width: "100%",
                height: `${pageHeight}mm`,
                border: 0,
                background: "#fff",
                }}
            />
            </Box>

          </Box>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setPreviewOpen(false)}
            disabled={printing}
          >
            {t("cancel")}
          </Button>

          <Button
            variant="contained"
            onClick={printReport}
            disabled={printing}
          >
            {printing ? t("loading") : t("print")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const mapStateToProps = (state) => ({
  _store: state.pos.defStore,
});

const mapDispatchToProps = {
  _get: actions.get,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ReportPrintButton);
